import React from "react";
import PasswordChange from "../../types/passwords/PasswordChange";
import PasswordConfig from "../../types/passwords/PasswordConfig";
import Datastore from "../../communication/Datastore";
import TTSDebouncer from "../../foundation/TTSDebouncer";

import "./PasswordEditor.scss";

interface PasswordEditorProps {
    slim?: boolean;
    className?: string;
    confirmCurrent?: boolean;
    password: PasswordConfig;
    passwordsChanged: (passwords: PasswordChange) => void;
}

interface PasswordEditorState {
    passwordEntry0: React.RefObject<HTMLInputElement>;
    passwordEntry1: React.RefObject<HTMLInputElement>;
    passwordEntry2: React.RefObject<HTMLInputElement>;
    passwordfInput0: string;
    passwordfInput1: string;
    passwordfInput2: string;
    passwordValidationResult: number[];
    canSubmitPassword: boolean;
    updatePassword1Debounce: TTSDebouncer;
    updatePassword2Debounce: TTSDebouncer;
}

class PasswordEditor extends React.Component<
    PasswordEditorProps,
    PasswordEditorState
> {
    state = {
        passwordEntry0: React.createRef<HTMLInputElement>(),
        passwordEntry1: React.createRef<HTMLInputElement>(),
        passwordEntry2: React.createRef<HTMLInputElement>(),
        updatePassword1Debounce: new TTSDebouncer(500),
        updatePassword2Debounce: new TTSDebouncer(500),
        passwordValidationResult: [-1],
    } as PasswordEditorState;

    componentDidMount(): void {
        if (this.props.confirmCurrent)
            setTimeout(() => this.state.passwordEntry0.current?.focus(), 200);
        else setTimeout(() => this.state.passwordEntry1.current?.focus(), 200);
    }

    updatePassword0(value: string) {
        this.setState(() => ({ passwordfInput0: value }));

        this.props.passwordsChanged({
            currentPassword: this.state.passwordfInput0,
            updatedPassword: this.state.passwordfInput1,
            valid: this.canSubmitPassword(value),
        } as PasswordChange);
    }
    updatePassword1(value: string) {
        this.setState(() => ({ passwordfInput1: value }));

        this.state.updatePassword1Debounce.call(async () => {
            let current = await this.isPasswordValid(value);
            this.props.passwordsChanged({
                currentPassword: this.state.passwordfInput0,
                updatedPassword: this.state.passwordfInput1,
                valid: this.canSubmitPassword(
                    this.state.passwordfInput0,
                    current,
                ),
            } as PasswordChange);
        });
    }
    updatePassword2(value: string) {
        this.setState(() => ({ passwordfInput2: value }));

        this.state.updatePassword2Debounce.call(() => {
            let current = this.doPasswordsMatch();
            this.props.passwordsChanged({
                currentPassword: this.state.passwordfInput0,
                updatedPassword: this.state.passwordfInput1,
                valid: this.canSubmitPassword(
                    this.state.passwordfInput0 ?? "",
                    current,
                ),
            } as PasswordChange);
        });
    }
    async isPasswordValid(
        value: string,
        force: boolean = false,
    ): Promise<number[]> {
        let result: number[] = [];

        if (value.length > 2 || force) {
            result = await Datastore.postType<number[]>(
                "api/Authentication/Session/ValidatePassword",
                {
                    password: this.state.passwordfInput1,
                },
            );

            if (
                this.state.passwordfInput1.length > 0 &&
                this.state.passwordfInput1 !== this.state.passwordfInput2
            )
                result.push(100);

            this.setState(() => ({ passwordValidationResult: result }));
        } else if (!this.state.passwordValidationResult.includes(-1)) {
            result = [-1].concat(this.state.passwordValidationResult);

            this.setState(() => ({ passwordValidationResult: result }));
        }

        return result;
    }
    doPasswordsMatch(): number[] {
        let current = this.state.passwordValidationResult;

        if (
            current.includes(100) &&
            this.state.passwordfInput1 === this.state.passwordfInput2
        ) {
            current.splice(current.indexOf(100), 1);
            this.setState(() => ({ passwordValidationResult: current }));
        } else if (
            !current.includes(100) &&
            this.state.passwordfInput1 !== this.state.passwordfInput2
        ) {
            current.push(100);
            this.setState(() => ({ passwordValidationResult: current }));
        }

        return current;
    }
    canSubmitPassword(current: string, validationResult?: number[]): boolean {
        let result =
            (validationResult ?? this.state.passwordValidationResult).length ===
                0 &&
            (current.length > 0 || !this.props.confirmCurrent);
        this.setState(() => ({ canSubmitPassword: result }));

        return result;
    }

    passwordCriteriaClass(index: number): string {
        const result = [];

        if (this.state.passwordValidationResult.includes(index))
            result.push("red");
        else result.push("green");

        return result.join(" ");
    }
    passwordCriteriaGlyph(index: number): string {
        if (this.state.passwordValidationResult.includes(index)) return "error";
        else return "check";
    }

    render(): React.ReactNode {
        let classes = "password-change";

        if (this.props.slim) classes += " slim";
        if (this.props.className) classes += ` ${this.props.className}`;

        return (
            <div className={classes}>
                <div className="password-inputs">
                    {this.props.confirmCurrent && (
                        <input
                            ref={this.state.passwordEntry0}
                            type="password"
                            autoComplete="current-password"
                            className="password stand-alone current-password"
                            placeholder="Current Password"
                            value={this.state.passwordfInput0 ?? ""}
                            onChange={ev =>
                                this.updatePassword0(ev.target.value)
                            }
                        />
                    )}
                    <input
                        ref={this.state.passwordEntry1}
                        type="password"
                        autoComplete="new-password"
                        className="password stand-alone"
                        placeholder="New Password"
                        value={this.state.passwordfInput1 ?? ""}
                        onChange={ev => this.updatePassword1(ev.target.value)}
                    />
                    <input
                        type="password"
                        autoComplete="new-password"
                        className="password stand-alone"
                        placeholder="Confirm Password"
                        value={this.state.passwordfInput2 ?? ""}
                        onChange={ev => this.updatePassword2(ev.target.value)}
                    />
                </div>
                <div
                    className="password-feedback"
                    style={{ width: "80%" }}>
                    The password must meet the following criteria:
                    <ul className="no-style icon-check-list-large left-buffer">
                        <li>
                            <i className={this.passwordCriteriaClass(-1)}>
                                {this.passwordCriteriaGlyph(-1)}
                            </i>
                            <span className="section-title">
                                Minimum length of{" "}
                                {this.props.password?.passwordMinLength ?? 0}
                            </span>
                        </li>
                        {this.props.password?.passwordIncludeUpperLetter && (
                            <li>
                                <i className={this.passwordCriteriaClass(0)}>
                                    {this.passwordCriteriaGlyph(0)}
                                </i>
                                <span className="section-title">
                                    Contains at least one upper case letter
                                </span>
                            </li>
                        )}
                        {this.props.password?.passwordIncludeLowerLetter && (
                            <li>
                                <i className={this.passwordCriteriaClass(1)}>
                                    {this.passwordCriteriaGlyph(1)}
                                </i>
                                <span className="section-title">
                                    Contains at least one lower case letter
                                </span>
                            </li>
                        )}
                        {this.props.password?.passwordIncludeNumber && (
                            <li>
                                <i className={this.passwordCriteriaClass(2)}>
                                    {this.passwordCriteriaGlyph(2)}
                                </i>
                                <span className="section-title">
                                    Contains at least one number
                                </span>
                            </li>
                        )}
                        {this.props.password?.passwordIncludeSpecial && (
                            <li>
                                <i className={this.passwordCriteriaClass(3)}>
                                    {this.passwordCriteriaGlyph(3)}
                                </i>
                                <span className="section-title">
                                    Contains at least special character
                                </span>
                            </li>
                        )}
                        {this.props.password?.passwordExcludeNames && (
                            <li>
                                <i className={this.passwordCriteriaClass(4)}>
                                    {this.passwordCriteriaGlyph(4)}
                                </i>
                                <span className="section-title">
                                    Does not contain your username, first name
                                    or last name
                                </span>
                            </li>
                        )}
                        <li>
                            <i className={this.passwordCriteriaClass(100)}>
                                {this.passwordCriteriaGlyph(100)}
                            </i>
                            <span className="section-title">
                                Entered passwords match
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        );
    }
}

export default PasswordEditor;
